import React from 'react';
import { Box, SxProps } from '@mui/material';
import { styled, Theme } from '@mui/material/styles';
import SandboxLikeSummary from './SandboxLikeSummary';
import useBoardDetailCommentCount from '@/components/Board/BoardDetail/Feedback/BoardDetailComment/useBoardDetailCommentCount';

type SandboxBottomBarProps = {
  documentId: string;
  commentGroupId: string;
  isLike: boolean;
  isLikePending?: boolean;
  attachmentCount: number;
  disabled?: boolean;
  isMobile?: boolean;
  showAttachmentButton?: boolean;
  onLikeClick?: () => void;
  onCommentClick?: () => void;
  onAttachmentClick?: () => void;
  sx?: SxProps<Theme>;
};

const BottomBarCommentIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M18.01 15.4821C18.8538 14.0225 19.1374 12.3056 18.8077 10.6521C18.4781 8.99861 17.5577 7.52171 16.2187 6.49727C14.8796 5.47283 13.2133 4.97093 11.5312 5.08534C9.84906 5.19975 8.26612 5.92264 7.07807 7.11896C5.89002 8.31528 5.17814 9.9032 5.0754 11.5861C4.97267 13.269 5.48612 14.9317 6.51982 16.2636C7.55353 17.5956 9.03679 18.5057 10.6925 18.8238C12.3482 19.142 14.0632 18.8465 15.5169 17.9926L18.9433 18.9488L18.01 15.4821Z"
      stroke="#000000"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

const BottomBarAttachmentIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M19.3747 11.8481L12.3292 18.8935C10.4116 20.8112 7.53565 21.0376 5.5805 19.0825C3.66288 17.1648 3.9121 14.3867 5.86725 12.4316L13.7871 4.51176C14.9992 3.29964 16.9506 3.29964 18.1627 4.51176C19.3748 5.72387 19.3748 7.67527 18.1627 8.88739L10.1038 16.9463C9.49967 17.5504 8.52015 17.5504 7.916 16.9463C7.31185 16.3421 7.31185 15.3626 7.916 14.7585L15.1005 7.57395"
      stroke="#000000"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);

const BottomBarContainer = styled('div')<{ isMobile?: boolean }>(
  ({ isMobile }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: isMobile
      ? '12px 16px max(env(safe-area-inset-bottom), 12px)'
      : '12px 16px',
    background: 'var(--dk-bg, #FFFFFF)',
    borderTop: '1px solid var(--dk-line, #E7E9EC)',
  }),
);

const VerticalDivider = styled('div')({
  width: '1px',
  height: '20px',
  background: 'var(--dk-bg-strong, #E7E9EC)',
  flexShrink: 0,
});

const StatButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  padding: '4px 8px 4px 4px',
  borderRadius: '4px',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--dk-text, #000000)',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  flexShrink: 0,
  userSelect: 'none',
  '&:hover': {
    background: 'var(--dk-bg-muted, #F3F5F6)',
  },
  '&:disabled': {
    cursor: 'default',
    opacity: 0.6,
    '&:hover': {
      background: 'none',
    },
  },
});

const SandboxBottomBar: React.FC<SandboxBottomBarProps> = ({
  documentId,
  isLike,
  isLikePending,
  commentGroupId,
  attachmentCount,
  disabled,
  isMobile,
  showAttachmentButton = true,
  onLikeClick,
  onCommentClick,
  onAttachmentClick,
  sx,
}) => {
  const { totalCount: commentCount } =
    useBoardDetailCommentCount(commentGroupId);

  return (
    <Box sx={sx}>
      <BottomBarContainer isMobile={isMobile}>
        <SandboxLikeSummary
          documentId={documentId}
          isLike={isLike}
          isLikePending={isLikePending}
          disabled={disabled}
          onLikeClick={onLikeClick}
          onSummaryClick={onCommentClick}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <VerticalDivider />
        <StatButton
          type="button"
          disabled={disabled}
          onClick={onCommentClick}
          aria-label="댓글"
        >
          <BottomBarCommentIcon />
          <span>{commentCount}</span>
        </StatButton>
        {showAttachmentButton && attachmentCount > 0 && (
          <StatButton
            type="button"
            disabled={disabled}
            onClick={onAttachmentClick}
            aria-label="첨부파일"
          >
            <BottomBarAttachmentIcon />
            <span>{attachmentCount}</span>
          </StatButton>
        )}
      </BottomBarContainer>
    </Box>
  );
};

export default SandboxBottomBar;
